/**
 * Shared error-boundary fallback (R-18.63): a clear message, a "Try again" button that
 * calls `resetErrorBoundary`, and a link home — never a blank white screen (R-18.69).
 * Used by both the top-level boundary and per-route boundaries (R-18.59/R-18.62).
 */

import { Button, Result, Space } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { FallbackProps } from 'react-error-boundary';

import {
  isStaleChunkError,
  reloadForStaleChunk,
  STALE_CHUNK_RETRY_INTERVAL_MS,
} from '@/shared/lib/staleChunkReload';

interface ErrorFallbackProps extends FallbackProps {
  isRouteLevel?: boolean;
}

export function ErrorFallback({
  error,
  resetErrorBoundary,
  isRouteLevel = false,
}: ErrorFallbackProps): React.JSX.Element {
  const { t } = useTranslation();
  // A stale-chunk failure is cached by React.lazy — resetting the boundary re-renders
  // into the same rejected import; only a full reload fetches the new chunk set.
  const staleChunk = isStaleChunkError(error);
  const retry = staleChunk ? () => window.location.reload() : resetErrorBoundary;

  // Hands-off recovery: while the fallback shows because of a stale chunk set, keep
  // retrying the reload on the guard cadence — the tab heals on its own as soon as a
  // fresh index.html is served, no user action needed.
  useEffect(() => {
    if (!staleChunk) {
      return;
    }
    const timer = window.setInterval(() => {
      reloadForStaleChunk();
    }, STALE_CHUNK_RETRY_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [staleChunk]);

  return (
    <Result
      status="error"
      title={isRouteLevel ? t('error.routeTitle') : t('error.boundaryTitle')}
      subTitle={t('error.boundaryMessage')}
      extra={
        <Space>
          <Button type="primary" onClick={retry}>
            {t('actions.tryAgain')}
          </Button>
          <Link to="/">
            <Button>{t('actions.goHome')}</Button>
          </Link>
        </Space>
      }
    />
  );
}
